import * as Dialog from "@radix-ui/react-dialog";
import { useMutation } from "@tanstack/react-query";
import { CircleAlert, ExternalLink, Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useTenant } from "@/components/AppShell";
import { Badge, Button, cx, Input } from "@/components/ui";
import { api, ApiError, type ChannelOrderHit, type ChannelOrderImport, type ChannelOrderQuery, type MarketplaceKind } from "@/lib/api";
import { intlLocale, t, msg } from "@/lib/i18n";

import { serverText } from "@/lib/server-text";
const errMsg = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Abruf fehlgeschlagen") : "Abruf fehlgeschlagen");

/** Wie heißen die Nummern im jeweiligen Kanal? */
const NUMBER_HINT: Record<MarketplaceKind | "shopify", { label: string; placeholder: string }> = {
  shopify: { label: msg("Bestellnummern"), placeholder: "#1042\n#1043" },
  odoo: { label: msg("Lieferschein-, Auftrags- oder Kundenreferenz-Nummern"), placeholder: msg("WH/OUT/00012\nS00042") },
  woocommerce: { label: msg("Bestellnummern"), placeholder: "1042\n1043" },
  shopware: { label: msg("Bestellnummern"), placeholder: "10042\n10043" },
  kaufland: { label: msg("Kaufland-Bestellnummern (ID der Bestellung)"), placeholder: msg("MPGKD9X") },
  amazon: { label: msg("Amazon-Bestellnummern"), placeholder: "302-1234567-1234567" },
};

const day = (d: Date) => d.toISOString().slice(0, 10);
const parseNumbers = (s: string, max = 100) => [...new Set(s.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean))].slice(0, max);

/**
 * Aufträge gezielt abrufen – zum Testen oder um ältere nachzuholen. Erst Vorschau (mit Status im Kanal, bereits
 * übernommene, unbekannte Artikel), dann nur die ausgewählten übernehmen. Der Server holt beim Übernehmen frisch ab.
 */
export function FetchOrdersDialog({ open, onClose, channelId, kind, label }: { open: boolean; onClose: () => void; channelId: string; kind: MarketplaceKind | "shopify"; label: string }) {
  const shopify = kind === "shopify";
  const { tenantId, tenant } = useTenant();
  const [mode, setMode] = useState<"numbers" | "range">("numbers");
  const [text, setText] = useState("");
  const [from, setFrom] = useState(() => day(new Date(Date.now() - 7 * 86_400_000)));
  const [to, setTo] = useState(() => day(new Date()));
  const [query, setQuery] = useState<ChannelOrderQuery | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<ChannelOrderImport | null>(null);

  const find = useMutation({
    mutationFn: (q: ChannelOrderQuery) => (shopify ? api.channels.findShopifyOrders(tenantId, channelId, q) : api.channels.findChannelOrders(tenantId, channelId, q)),
    onSuccess: (r) => setSelected(new Set(r.orders.filter((o) => o.shippable && !o.known && !o.blocked).map((o) => o.externalId))),
    onError: (e) => toast.error(errMsg(e)),
  });
  const take = useMutation({
    mutationFn: () => (shopify ? api.channels.importShopifyOrders : api.channels.importChannelOrders)(tenantId, channelId, query!, [...selected]),
    onSuccess: (r) => {
      setResult(r);
      if (r.created.length) toast.success(`${r.created.length} ${r.created.length === 1 ? "Auftrag" : "Aufträge"} übernommen`);
      if (r.requested?.length) toast.success(`${r.requested.length} bei Shopify angefordert`);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const search = () => {
    const q: ChannelOrderQuery = mode === "numbers" ? { numbers: parseNumbers(text, shopify ? 25 : 100) } : { from: new Date(`${from}T00:00:00`).toISOString(), to: new Date(`${to}T23:59:59`).toISOString() };
    setQuery(q);
    setResult(null);
    find.mutate(q);
  };
  const reset = () => { setQuery(null); setResult(null); find.reset(); take.reset(); setSelected(new Set()); };
  const close = () => { reset(); setText(""); onClose(); };

  const data = find.data;
  const choosable = (o: ChannelOrderHit) => !o.known && !o.blocked && o.status !== "cancelled" && o.lines.length > 0;
  const chosen = useMemo(() => (data?.orders ?? []).filter((o) => selected.has(o.externalId)), [data, selected]);
  const forced = chosen.filter((o) => !o.shippable);
  const unknownSkus = [...new Set(chosen.flatMap((o) => o.lines.filter((l) => !l.known).map((l) => l.sku)))];
  const rangeDays = (Date.parse(to) - Date.parse(from)) / 86_400_000;
  const canSearch = mode === "numbers" ? parseNumbers(text, shopify ? 25 : 100).length > 0 : !!from && !!to && rangeDays >= 0 && rangeDays <= 31;
  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && close()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[90vh] w-[min(860px,calc(100vw-32px))] overflow-y-auto rounded-2xl border border-border bg-surface p-6 shadow-2xl animate-pop-in">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[17px] font-semibold">{t("Aufträge gezielt abrufen")}</Dialog.Title>
              <Dialog.Description className="text-fg-muted mt-1 text-[13px]">{t("Aus")} {t(label)} {t("– zum Testen oder um ältere Aufträge nachzuholen.")} {shopify ? t("Connect fordert für den Logiful-Teil das Fulfillment an (wie „Fulfillment anfordern“ in Shopify); danach laufen die Aufträge wie gewohnt ans Lager.") : t("Übernommene Aufträge laufen danach wie gewohnt durch Prüfung und Regeln ans Lager.")}</Dialog.Description>
            </div>
            <Dialog.Close className="size-8 grid place-items-center rounded-lg text-fg-muted hover:bg-surface-2" aria-label={t("Schließen")}><X className="size-4" /></Dialog.Close>
          </div>

          {result ? (
            <div className="mt-5 space-y-3">
              {result.created.length > 0 && (
                <div className="rounded-xl border border-success/25 bg-success-soft/50 p-4 text-[13px]">
                  <p className="font-medium">{result.created.length} {result.created.length === 1 ? t("Auftrag") : t("Aufträge")} {t("übernommen")}{tenant.testModeSince ? t(" – Testmodus: werden geprüft, aber nicht ans Lager übergeben") : ""}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {result.created.map((o) => <Link key={o.id} to={`/orders?open=${o.id}`} onClick={close} className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-0.5 font-mono text-[12px] hover:bg-surface-2">{o.orderNumber}<ExternalLink className="size-3" /></Link>)}
                  </div>
                </div>
              )}
              {!!result.requested?.length && (
                <div className="rounded-xl border border-success/25 bg-success-soft/50 p-4 text-[13px]">
                  <p className="font-medium">{result.requested.length} {result.requested.length === 1 ? t("Bestellung") : t("Bestellungen")} {t("bei Shopify angefordert:")} <span className="font-mono">{[...new Set(result.requested.map((r) => r.number))].join(", ")}</span></p>
                  <p className="mt-1 text-fg-muted text-[12.5px]">{t("Sie erscheinen in etwa einer Minute unter Aufträge und laufen dann wie gewohnt durch Prüfung und Regeln ans Lager.")}</p>
                </div>
              )}
              {result.skipped.length > 0 && (
                <div className="rounded-xl border border-border p-4 text-[12.5px]">
                  <p className="font-medium">{t("Nicht übernommen")}</p>
                  <ul className="mt-1 text-fg-muted">{result.skipped.map((s) => <li key={s.number}><span className="font-mono">{s.number}</span> – {serverText(s.reason)}</li>)}</ul>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={reset}>{t("Weitere abrufen")}</Button>
                <Button variant="primary" onClick={close}>{t("Fertig")}</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="mt-5 inline-flex rounded-lg bg-surface-2 p-0.5">
                {(["numbers", "range"] as const).map((m) => (
                  <button key={m} type="button" onClick={() => setMode(m)} className={cx("h-7 px-3 rounded-md text-[12.5px] font-medium", mode === m ? "bg-surface shadow-sm" : "text-fg-muted hover:text-fg")}>{m === "numbers" ? t("Nach Nummer") : t("Nach Zeitraum")}</button>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                {mode === "numbers" ? (
                  <label className="block flex-1 min-w-[260px]">
                    <span className="text-[12px] font-medium text-fg-muted">{t(NUMBER_HINT[kind].label)}</span>
                    <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder={t(NUMBER_HINT[kind].placeholder)} className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 font-mono text-[13px] focus:outline-none focus:ring-2 focus:ring-ink/20" />
                    <span className="block text-[11.5px] text-fg-subtle">{t("Eine je Zeile oder durch Komma getrennt, bis zu")} {shopify ? 25 : 100}.{shopify ? t(" Shopify zeigt Bestellungen, die älter als 60 Tage sind, nur mit der Zusatzberechtigung „alle Bestellungen lesen“.") : ""}</span>
                  </label>
                ) : (
                  <>
                    <label className="block"><span className="text-[12px] font-medium text-fg-muted">{t("Bestellt von")}</span><Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="mt-1" /></label>
                    <label className="block"><span className="text-[12px] font-medium text-fg-muted">{t("bis")}</span><Input type="date" value={to} min={from} max={day(new Date())} onChange={(e) => setTo(e.target.value)} className="mt-1" /></label>
                    {rangeDays > 31 && <span className="text-[12px] text-danger pb-2">{t("höchstens 31 Tage")}</span>}
                  </>
                )}
                <Button variant="primary" icon={<Search className="size-4" />} loading={find.isPending} disabled={!canSearch} onClick={search}>{t("Suchen")}</Button>
              </div>

              {data && (
                <div className="mt-5">
                  {data.notFound.length > 0 && (
                    <div className="mb-2 text-[12.5px] text-warning">
                      <p className="flex items-center gap-1.5"><CircleAlert className="size-4" /> {t("Nicht gefunden:")} <span className="font-mono">{data.notFound.join(", ")}</span></p>
                      {data.notFound.some((n) => data.notFoundReasons?.[n]) && (
                        <ul className="mt-1 ml-5 space-y-0.5 text-fg-muted">
                          {data.notFound.filter((n) => data.notFoundReasons?.[n]).map((n) => <li key={n}><span className="font-mono">{n}</span>: {data.notFoundReasons![n]}</li>)}
                        </ul>
                      )}
                    </div>
                  )}
                  {data.truncated && <p className="mb-2 text-[12.5px] text-fg-muted">{t("Es gibt mehr Treffer – angezeigt werden die ersten 100. Bitte den Zeitraum verkleinern.")}</p>}
                  {data.orders.length === 0 ? (
                    <p className="rounded-xl border border-border px-4 py-6 text-center text-[13px] text-fg-muted">{t("Keine Aufträge gefunden.")}</p>
                  ) : (
                    <div className="rounded-xl border border-border overflow-hidden">
                      <ul className="divide-y divide-border max-h-[45vh] overflow-y-auto">
                        {data.orders.map((o) => {
                          const can = choosable(o);
                          return (
                            <li key={o.externalId}>
                              <label className={cx("flex items-start gap-3 px-4 py-2.5 text-[13px]", can ? "cursor-pointer hover:bg-surface-2/50" : "opacity-70")}>
                                <input type="checkbox" className="mt-1 accent-ink" disabled={!can} checked={selected.has(o.externalId)} onChange={() => toggle(o.externalId)} />
                                <span className="min-w-0 flex-1">
                                  <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                    <span className="font-mono font-medium">{o.number}</span>
                                    <span className="text-fg-subtle text-[12px]">{new Date(o.placedAt).toLocaleDateString(intlLocale())}</span>
                                    {o.channelState && <span className="text-[12px] text-fg-muted">{o.channelState}</span>}
                                  </span>
                                  <span className="block text-[12px] text-fg-muted truncate">{o.recipient || "–"} · {o.city} {o.country !== "DE" ? o.country : ""}</span>
                                  {o.blocked && <span className="block text-[11.5px] text-warning">{o.blocked}</span>}
                                  <span className="block text-[11.5px] text-fg-subtle truncate">
                                    {o.lines.length ? o.lines.map((l) => `${l.quantity}× ${l.sku}`).join(", ") : t("keine lagerfähigen Positionen")}
                                    {o.lines.some((l) => !l.known) && <span className="text-warning"> · {o.lines.filter((l) => !l.known).length} {t("Artikel unbekannt")}</span>}
                                  </span>
                                </span>
                                <span className="shrink-0 pt-0.5">
                                  {o.known ? <Link to={`/orders?open=${o.known.id}`} onClick={close}><Badge tone="neutral">{t("schon übernommen ·")} {o.known.orderNumber}</Badge></Link>
                                    : o.status === "cancelled" ? <Badge tone="danger">{t("storniert")}</Badge>
                                    : o.blocked ? <Badge tone="neutral">{t("nicht möglich")}</Badge>
                                    : !o.lines.length ? <Badge tone="neutral">{t("nichts zu versenden")}</Badge>
                                    : o.shippable ? <Badge tone="success">{t("versandbereit")}</Badge>
                                    : <Badge tone="warning">{t("nicht versandbereit")}</Badge>}
                                </span>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {forced.length > 0 && (
                    <p className="mt-3 rounded-xl border border-warning/30 bg-warning-soft/50 px-3 py-2 text-[12.5px]">
                      <b>{forced.length} {forced.length === 1 ? t("ausgewählter Auftrag ist") : t("ausgewählte Aufträge sind")} {t("im Kanal nicht versandbereit")}</b> {t("(z. B. schon versendet oder nicht bezahlt). Bitte nur übernehmen, wenn die Ware wirklich noch aus dem Logiful-Lager gehen soll.")}
                    </p>
                  )}
                  {unknownSkus.length > 0 && <p className="mt-2 text-[12.5px] text-fg-muted">{t("Unbekannte Artikel (")}{unknownSkus.slice(0, 5).join(", ")}{unknownSkus.length > 5 ? " …" : ""}{t(") – die Aufträge warten dann unter Handlungsbedarf, bis der Artikel angelegt ist.")}</p>}
                  {tenant.testModeSince && <p className="mt-2 text-[12.5px] text-info">{t("Testmodus aktiv: Aufträge werden geprüft, aber nicht ans Lager übergeben.")}</p>}

                  <div className="mt-4 flex items-center justify-end gap-2">
                    <Button variant="ghost" onClick={close}>{t("Abbrechen")}</Button>
                    <Button variant="primary" disabled={!chosen.length} loading={take.isPending} onClick={() => take.mutate()}>{chosen.length ? `${chosen.length} ${chosen.length === 1 ? "Auftrag" : "Aufträge"} ${shopify ? "anfordern" : "übernehmen"}` : t("Aufträge auswählen")}</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
